// Работното време на заведението (Конфигурация → Работно време) за чистата
// логика извън React. Ползва се САМО на две места:
//   • часовете в справките с час — периодът по подразбиране
//     (`lib/businessDay.ts`) и менюто „смяна" (`lib/reportShift.ts`);
//   • часовете, в които формата за резервации приема начало —
//     `pages/reservations/logic/workingSlots.ts`.
// Нищо друго не бива да го чете.
//
// Стойността идва от `/user/info` (`configuration.workingHours`); `store.ts` я
// подава тук при всяка промяна на store-а. null — още не е настроено: тогава
// и двете места остават на досегашните си часове.
//
// Денят има от една до три смени — една след друга или с почивка между тях.
// Работният ден е от отварянето на първата до затварянето на последната.

import type { WorkingDay } from "../api/configuration";

const DAYS_IN_WEEK = 7;
const DAY_MINUTES = 24 * 60;

let currentWorkingHours: WorkingDay[] | null = null;

/**
 * Седмицата, ако е във вида със смени; иначе null (като ненастроено). Кешът
 * на самоличността (`hydrateUserInfo`) може да е отпреди смените — тогава
 * дните са без `shifts`.
 */
export const validWorkingHours = (workingHours: WorkingDay[] | null | undefined): WorkingDay[] | null =>
  workingHours?.length === DAYS_IN_WEEK &&
  workingHours.every((day) => Array.isArray(day.shifts) && day.shifts.length > 0)
    ? workingHours
    : null;

export const setWorkingHours = (workingHours: WorkingDay[] | null | undefined) => {
  currentWorkingHours = validWorkingHours(workingHours);
};

export const getWorkingHours = (): WorkingDay[] | null => currentWorkingHours;

/** "HH:MM" → минута от денонощието. */
export const minutesOfTime = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

/** Смяна като минути от полунощта на деня си; след полунощ — над 1440. */
export interface ShiftSpan {
  start: number;
  end: number;
}

/**
 * Смените на деня като минути от полунощта му (огледало на BE
 * `WorkingDay.spans`): отваряне преди предишното затваряне и затваряне до
 * или преди отварянето на смяната са на следващата дата (+24 ч).
 */
export const shiftSpansOf = (day: WorkingDay): ShiftSpan[] => {
  let previous = -1;
  return day.shifts.map((shift) => {
    let start = minutesOfTime(shift.opensAt);
    while (start < previous) start += DAY_MINUTES;
    let end = minutesOfTime(shift.closesAt);
    while (end <= start) end += DAY_MINUTES;
    previous = end;
    return { start, end };
  });
};

/** Отварянето на деня — на първата смяна. */
export const opensAtOf = (day: WorkingDay): string => day.shifts[0].opensAt;

/** Затварянето на деня — на последната смяна (след полунощ — на следващата дата). */
export const closesAtOf = (day: WorkingDay): string => day.shifts[day.shifts.length - 1].closesAt;

/** Денят затваря след полунощ (последната смяна свършва на следващата дата). */
export const crossesMidnight = (day: WorkingDay): boolean => {
  if (day.closed) return false;
  const spans = shiftSpansOf(day);
  return spans[spans.length - 1].end >= DAY_MINUTES;
};

/**
 * Работното време на деня от седмицата по JS `getDay()` (0 = неделя);
 * null — не е настроено. В настройката понеделник е първи.
 */
export const workingDayOf = (jsWeekday: number): WorkingDay | null =>
  currentWorkingHours?.[(jsWeekday + 6) % DAYS_IN_WEEK] ?? null;
